import { get } from '@rails/request.js'

const MIN_SHARE = 0.6
const MIN_LEVEL = -60
const MIN_BEEP_SECONDS = 0.2
const MAX_TONE_SECONDS = 1.5
const FALLBACK_SECONDS = 30

export default class VoicemailDrop {
  constructor({ call, audioUrl, maxSeconds, onFinish }) {
    this.call = call
    this.audioUrl = audioUrl
    this.maxSeconds = maxSeconds
    this.onFinish = onFinish
    this.finished = false
    this.playing = false
    this.played = false
    this.toneStart = null
    this.toneFrequency = null
  }

  async start() {
    this.call.muteAudio()
    this.listener = new Audio()
    this.listener.muted = true
    this.listener.hidden = true
    this.listener.srcObject = this.call.remoteStream
    document.body.appendChild(this.listener)
    this.listener.play().catch(() => {})

    this.context = new AudioContext()
    const response = await get(this.audioUrl).catch(() => null)
    if (!response?.ok) return this.hangup()
    this.recording = await this.context.decodeAudioData(await response.response.arrayBuffer()).catch(() => null)
    if (!this.recording || this.finished) return this.hangup()

    this.listen()
  }

  listen() {
    const source = this.context.createMediaStreamSource(this.call.remoteStream)
    this.analyser = this.context.createAnalyser()
    this.analyser.fftSize = 2048
    this.analyser.smoothingTimeConstant = 0
    this.processor = this.context.createScriptProcessor(2048, 1, 1)
    const silence = this.context.createGain()
    silence.gain.value = 0
    source.connect(this.analyser)
    source.connect(this.processor)
    this.processor.connect(silence)
    silence.connect(this.context.destination)

    this.bins = new Float32Array(this.analyser.frequencyBinCount)
    this.binHz = this.context.sampleRate / this.analyser.fftSize
    this.startedAt = this.context.currentTime
    this.processor.addEventListener('audioprocess', () => this.inspect())
  }

  inspect() {
    if (this.finished) return
    const elapsed = this.context.currentTime - this.startedAt
    if (this.maxSeconds && elapsed >= this.maxSeconds) return this.hangup()
    if (this.playing) return

    this.analyser.getFloatFrequencyData(this.bins)
    const lowBin = Math.floor(200 / this.binHz)
    const highBin = Math.ceil(4000 / this.binHz)
    let total = 0
    let peak = 0
    let peakIndex = lowBin
    for (let index = lowBin; index <= highBin; index++) {
      const power = 10 ** (this.bins[index] / 10)
      total += power
      if (power > peak) {
        peak = power
        peakIndex = index
      }
    }
    let peakBand = 0
    for (let index = peakIndex - 2; index <= peakIndex + 2; index++) peakBand += 10 ** (this.bins[index] / 10)

    const share = total > 0 ? peakBand / total : 0
    const level = total > 0 ? 10 * Math.log10(total) : -Infinity
    const frequency = peakIndex * this.binHz
    const tonal = share >= MIN_SHARE && level >= MIN_LEVEL
    const sameTone = this.toneFrequency !== null && Math.abs(frequency - this.toneFrequency) <= this.binHz * 2

    if (tonal && (this.toneStart === null || sameTone)) {
      if (this.toneStart === null) {
        this.toneStart = elapsed
        this.toneFrequency = frequency
      }
      if (elapsed - this.toneStart >= MAX_TONE_SECONDS) this.play()
      return
    }

    if (this.toneStart !== null) {
      if (elapsed - this.toneStart >= MIN_BEEP_SECONDS) return this.play()
      this.toneStart = null
      this.toneFrequency = null
    }

    if (elapsed >= FALLBACK_SECONDS) this.play()
  }

  async play() {
    this.playing = true
    const sender = this.call.peer?.instance?.getSenders().find((candidate) => candidate.track?.kind === 'audio')
    if (!sender) return this.hangup()

    const player = this.context.createBufferSource()
    player.buffer = this.recording
    const destination = this.context.createMediaStreamDestination()
    player.connect(destination)

    try {
      await sender.replaceTrack(destination.stream.getAudioTracks()[0])
    } catch (error) {
      return this.hangup()
    }

    player.addEventListener('ended', () => this.hangup())
    player.start()
    this.played = true
  }

  hangup() {
    if (!['hangup', 'destroy'].includes(this.call.state)) this.call.hangup()
    this.stop()
  }

  handleUpdate(call) {
    if (call.state === 'hangup' || call.state === 'destroy') this.stop()
  }

  stop() {
    if (this.finished) return
    this.finished = true
    this.processor?.disconnect()
    this.context?.close()
    this.listener?.remove()
    this.onFinish(this.played)
  }
}
